import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { listCandidates, listSessions } from '../api/sessions';
import { listSchedules } from '../api/schedules';
import { listAllOrganizations } from '../api/orgs';
import { authStore } from '../api/client';
import { formatDateTime, riskColor, statusColor } from '../utils/format';
import Avatar from '../components/ui/Avatar';
import EmptyState from '../components/ui/EmptyState';
import Spinner from '../components/ui/Spinner';
import MockPerformanceSection from '../components/candidate/MockPerformanceSection';

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function normalizeSessionListBody(resData) {
  if (Array.isArray(resData)) return resData;
  return resData?.content ?? [];
}

/** Friendly "3 days ago" string with a safe fallback. */
function relativeTime(iso) {
  if (!iso) return '-';
  try {
    return `${formatDistanceToNow(new Date(iso))} ago`;
  } catch {
    return formatDateTime(iso);
  }
}

/** Derive a display name from the email localpart when no real name is present. */
function nameFromEmail(email) {
  if (!email) return '';
  const local = email.split('@')[0] || email;
  return local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Tiny Tailwind badge - re-implemented locally so the page is self-contained. */
function Badge({ label, className = '' }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${className}`}
    >
      {label}
    </span>
  );
}

/** Auto-dismissing toast (3s). */
function Toast({ message, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white text-sm font-medium
                 px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2"
    >
      <span aria-hidden="true">✓</span>
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sortable column header
// ---------------------------------------------------------------------------

function SortHeader({ children, sortKey, sortBy, sortDir, onSort, className = '' }) {
  const active = sortBy === sortKey;
  const arrow  = !active ? '' : sortDir === 'asc' ? ' ↑' : ' ↓';
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer
                  select-none transition-colors ${active ? 'text-teal-600 font-bold' : 'text-ink-faint hover:text-ink'} ${className}`}
    >
      {children}{arrow}
    </th>
  );
}

// ---------------------------------------------------------------------------
// Candidate profile side panel
// ---------------------------------------------------------------------------

/**
 * Slide-in panel showing the full history for a single candidate.
 *
 * Data model:
 *   - sessions:  client-side filtered from {@code allSessions} prop by candidateEmail
 *   - schedules: client-side filtered from {@code allSchedules} prop by candidateEmail
 *   - notes:     persisted in localStorage under "ig.notes.<candidateEmail>"
 */
function CandidateProfilePanel({
  candidate,
  allSessions,
  allSchedules,
  loading,
  onClose,
  onNavigate,
  onCopy,
}) {
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [notes,           setNotes]           = useState('');
  const [savedFlash,      setSavedFlash]      = useState(false);

  // Filter sessions + schedules client-side
  const sessions = useMemo(() => {
    const email = candidate?.candidateEmail?.toLowerCase();
    if (!email) return [];
    return (allSessions || [])
      .filter((s) => (s.candidateEmail || '').toLowerCase() === email)
      .sort((a, b) => new Date(b.createdAt || b.startedAt || 0) - new Date(a.createdAt || a.startedAt || 0));
  }, [candidate, allSessions]);

  const upcoming = useMemo(() => {
    const email = candidate?.candidateEmail?.toLowerCase();
    if (!email) return [];
    return (allSchedules || [])
      .filter((s) => (s.candidateEmail || '').toLowerCase() === email)
      .filter((s) => (s.status || '').toUpperCase() === 'SCHEDULED')
      .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  }, [candidate, allSchedules]);

  // Load private notes from localStorage when candidate changes
  useEffect(() => {
    if (!candidate?.candidateEmail) return;
    const key = `ig.notes.${candidate.candidateEmail}`;
    const stored = localStorage.getItem(key) || '';
    console.log('[CandidatesPage] loaded notes for', candidate.candidateEmail, '-', stored.length, 'chars');
    setNotes(stored);
    setShowAllSessions(false);
  }, [candidate]);

  const handleNotesBlur = useCallback(() => {
    if (!candidate?.candidateEmail) return;
    const key = `ig.notes.${candidate.candidateEmail}`;
    localStorage.setItem(key, notes);
    console.log('[CandidatesPage] notes saved for', candidate.candidateEmail);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  }, [candidate, notes]);

  if (!candidate) return null;

  const displayName  = candidate.candidateName || nameFromEmail(candidate.candidateEmail);
  const totalSess    = candidate.totalSessions ?? sessions.length;
  const totalFind    = candidate.totalFindings ?? 0;
  const visibleSess  = showAllSessions ? sessions : sessions.slice(0, 10);

  return (
    <>
      {/* Backdrop - click to close */}
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        role="dialog"
        aria-label="Candidate profile"
        className="fixed top-0 right-0 z-50 h-full w-[480px] max-w-full bg-white
                   shadow-2xl border-l border-slate-200 flex flex-col"
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <header className="px-6 py-5 border-b border-slate-200 flex items-start gap-4">
          <Avatar name={displayName} email={candidate.candidateEmail} size="lg" />

          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 truncate">
              {displayName}
            </h2>
            <button
              type="button"
              onClick={() => onCopy(candidate.candidateEmail)}
              className="mt-0.5 text-xs text-ink-faint hover:text-teal-600
                         transition-colors truncate block max-w-full text-left"
              title="Copy email"
            >
              {candidate.candidateEmail}
            </button>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-700 font-medium">{totalSess}</span>
              <span className="text-slate-400">sessions</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-700 font-medium">{totalFind}</span>
              <span className="text-slate-400">findings</span>
              <span className="text-slate-300">·</span>
              {candidate.worstRisk
                ? <Badge label={candidate.worstRisk} className={riskColor(candidate.worstRisk)} />
                : <span className="text-slate-400">no risk yet</span>}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition-colors text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-7">
          {loading && (
            <div className="py-6"><Spinner size="sm" label="Loading history..." /></div>
          )}

          {/* Upcoming interviews */}
          <section>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Upcoming interviews
            </h3>
            {upcoming.length === 0 ? (
              <p className="text-sm text-slate-400 italic">None scheduled.</p>
            ) : (
              <ul className="space-y-2">
                {upcoming.map((sch) => (
                  <li
                    key={sch.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-lg
                               border border-line hover:border-teal-300 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {sch.title || 'Untitled interview'}
                      </p>
                      <p className="text-xs text-slate-500">{formatDateTime(sch.scheduledAt)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onNavigate(`/interviews`)}
                      className="px-3 py-1 text-xs font-semibold text-white bg-teal-600
                                 hover:bg-teal-700 rounded transition-colors"
                    >
                      Open
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Session history */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Session history
              </h3>
              <span className="text-xs text-slate-400">{sessions.length} total</span>
            </div>

            {sessions.length === 0 ? (
              <p className="text-sm text-slate-400 italic">No completed sessions yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {visibleSess.map((s) => {
                  const isEnded = (s.status || '').toUpperCase() === 'ENDED';
                  return (
                    <li
                      key={s.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg
                                 border border-slate-200 hover:bg-slate-50 transition-colors"
                    >
                      <div className="min-w-0 flex items-center gap-3">
                        <code className="text-[11px] font-mono text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">
                          {s.code || s.id?.slice(0, 8)}
                        </code>
                        <div className="text-xs text-slate-500 whitespace-nowrap">
                          {relativeTime(s.createdAt || s.startedAt)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge label={s.status} className={statusColor(s.status)} />
                        <span className="text-[11px] text-slate-500 whitespace-nowrap">
                          {s.findingCount ?? 0} findings
                        </span>
                        {isEnded && (
                          <button
                            type="button"
                            onClick={() => onNavigate(`/report/${s.id}`)}
                            className="px-2 py-1 text-[11px] font-semibold text-teal-600
                                       hover:bg-teal-50 rounded transition-colors"
                          >
                            View Report
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {sessions.length > 10 && !showAllSessions && (
              <button
                type="button"
                onClick={() => setShowAllSessions(true)}
                className="mt-2 text-xs font-semibold text-teal-600 hover:text-teal-700"
              >
                Show all {sessions.length} sessions
              </button>
            )}
          </section>

          {/* Private notes */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Private notes (only you can see these)
              </h3>
              {savedFlash && (
                <span className="text-[11px] text-teal-600 font-medium">Saved</span>
              )}
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={handleNotesBlur}
              rows={5}
              placeholder="Add notes for this candidate..."
              className="w-full px-3 py-2 text-sm border border-line rounded-lg
                         focus:ring-2 focus:ring-teal-500 focus:border-teal-500
                         transition-shadow resize-y"
            />
          </section>

          {/* Mock interview performance (Phase 4 component, embedded per Phase 7 spec).
              Note: the underlying API scopes by JWT, so this shows the *viewer's*
              own mock history rather than per-candidate. Treated as a placeholder
              until the backend exposes a host-scoped per-candidate mock listing. */}
          <section>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Mock interview performance
            </h3>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <MockPerformanceSection />
            </div>
          </section>
        </div>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// CandidatesPage
// ---------------------------------------------------------------------------

/**
 * CandidatesPage
 *
 * Route: /candidates
 *
 * Shows every candidate that has had a session or scheduled interview with
 * the authenticated host, with a slide-in profile panel for full history.
 *
 * Data sources:
 *   GET /api/sessions/candidates  -> aggregated CandidateSummary list (table)
 *   GET /api/sessions             -> raw session list (for panel history)
 *   GET /api/schedules            -> raw schedule list (for upcoming section)
 */
export default function CandidatesPage() {
  // -- Table data --
  const [candidates, setCandidates] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // -- Side-data for the profile panel (loaded once, shared across opens) --
  const [allSessions,  setAllSessions]  = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);

  const role = authStore.role;
  const userEmail = authStore.user?.email || '';

  // -- UI state (search params needed before org filter state) --
  const [searchParams, setSearchParams] = useSearchParams();
  const [orgList, setOrgList] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(() => searchParams.get('orgId') || '');
  /** null = show all candidates (no session-derived filter); Set = restrict to those emails */
  const [scopeEmails, setScopeEmails] = useState(null);
  const [search,    setSearch]    = useState('');
  const [sortBy,    setSortBy]    = useState('lastSeen');
  const [sortDir,   setSortDir]   = useState('desc');
  const [selected,  setSelected]  = useState(null);
  const [toast,     setToast]     = useState(null);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearch(q);
  }, [searchParams]);

  useEffect(() => {
    const oid = searchParams.get('orgId') || '';
    setSelectedOrgId(oid);
  }, [searchParams]);

  useEffect(() => {
    if (role !== 'SUPER_ADMIN') return;
    listAllOrganizations()
      .then((res) => {
        const d = res.data;
        setOrgList(Array.isArray(d) ? d : (d?.content ?? []));
      })
      .catch((err) => console.error('[CandidatesPage] listAllOrganizations:', err.message));
  }, [role]);

  useEffect(() => {
    let cancelled = false;

    async function scopeCandidates() {
      if (role === 'ORG_ADMIN') {
        setScopeEmails(null);
        return;
      }
      if (role === 'SUPER_ADMIN' && !selectedOrgId) {
        setScopeEmails(null);
        return;
      }
      try {
        const params = { size: 800 };
        if (role === 'SUPER_ADMIN' && selectedOrgId) params.orgId = selectedOrgId;
        const res = await listSessions(params);
        let sessions = normalizeSessionListBody(res.data);
        if (role === 'INTERVIEWER') {
          const em = userEmail.toLowerCase();
          sessions = sessions.filter(
            (s) => (s.createdBy || s.hostEmail || '').toLowerCase() === em,
          );
        }
        const emails = new Set(
          sessions.map((s) => (s.candidateEmail || '').toLowerCase()).filter(Boolean),
        );
        if (!cancelled) setScopeEmails(emails);
      } catch {
        if (!cancelled) setScopeEmails(new Set());
      }
    }

    scopeCandidates();
    return () => { cancelled = true; };
  }, [role, selectedOrgId, userEmail]);

  useEffect(() => {
    setAllSessions([]);
    setAllSchedules([]);
  }, [selectedOrgId, role]);

  // ---------------------------------------------------------------------------
  // Initial load
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      console.log('[CandidatesPage] mount - loading candidates');
      try {
        const res  = await listCandidates();
        const list = Array.isArray(res.data) ? res.data : (res.data?.candidates ?? []);
        if (cancelled) return;
        console.log('[CandidatesPage] loaded', list.length, 'candidates');
        setCandidates(list);
      } catch (err) {
        if (cancelled) return;
        console.error('[CandidatesPage] listCandidates failed:', err.message);
        setError(err.message || 'Failed to load candidates.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---------------------------------------------------------------------------
  // Lazy-load sessions + schedules on first panel open (parallel fetch)
  // ---------------------------------------------------------------------------
  const ensurePanelData = useCallback(async () => {
    if (allSessions.length || allSchedules.length) return; // already loaded
    setPanelLoading(true);
    console.log('[CandidatesPage] panel data fetch - role:', role, 'org:', selectedOrgId || '(all)');
    try {
      const sessParams = { size: 300 };
      if (role === 'SUPER_ADMIN' && selectedOrgId) sessParams.orgId = selectedOrgId;

      let schedPromise;
      if (role === 'SUPER_ADMIN') {
        const sp = selectedOrgId ? { orgId: selectedOrgId } : {};
        schedPromise = listSchedules(sp);
      } else if (userEmail) {
        schedPromise = listSchedules(userEmail);
      } else {
        schedPromise = Promise.resolve({ data: [] });
      }

      const [sessRes, schedRes] = await Promise.all([
        listSessions(sessParams),
        schedPromise,
      ]);

      let sessList = normalizeSessionListBody(sessRes.data);
      if (role === 'INTERVIEWER') {
        const em = userEmail.toLowerCase();
        sessList = sessList.filter(
          (s) => (s.createdBy || s.hostEmail || '').toLowerCase() === em,
        );
      }

      const schedList = Array.isArray(schedRes.data)
        ? schedRes.data
        : (schedRes.data?.content ?? schedRes.data?.schedules ?? []);

      console.log('[CandidatesPage] panel data loaded -', sessList.length, 'sessions,',
                  schedList.length, 'schedules');
      setAllSessions(sessList);
      setAllSchedules(schedList);
    } catch (err) {
      console.error('[CandidatesPage] panel data fetch failed:', err.message);
    } finally {
      setPanelLoading(false);
    }
  }, [allSessions.length, allSchedules.length, role, selectedOrgId, userEmail]);

  // ---------------------------------------------------------------------------
  // Filter + sort
  // ---------------------------------------------------------------------------
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = candidates;
    if (scopeEmails !== null) {
      rows = rows.filter((c) =>
        scopeEmails.has((c.candidateEmail || '').toLowerCase()),
      );
    }
    if (q) {
      rows = rows.filter((c) => {
        const name = (c.candidateName || nameFromEmail(c.candidateEmail) || '').toLowerCase();
        return name.includes(q) || (c.candidateEmail || '').toLowerCase().includes(q);
      });
    }
    const dirMul = sortDir === 'asc' ? 1 : -1;
    rows = [...rows].sort((a, b) => {
      let av = a[sortBy];
      let bv = b[sortBy];
      if (sortBy === 'lastSeen' || sortBy === 'firstSeen') {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      }
      av = av ?? 0;
      bv = bv ?? 0;
      if (av < bv) return -1 * dirMul;
      if (av > bv) return  1 * dirMul;
      return 0;
    });
    return rows;
  }, [candidates, scopeEmails, search, sortBy, sortDir]);

  const handleSort = useCallback((key) => {
    setSortBy((prevKey) => {
      if (prevKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      setSortDir('desc');
      return key;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Open profile panel
  // ---------------------------------------------------------------------------
  const handleOpen = useCallback(async (candidate) => {
    console.log('[CandidatesPage] open profile for', candidate.candidateEmail);
    setSelected(candidate);
    await ensurePanelData();
  }, [ensurePanelData]);

  const handleCopyEmail = useCallback(async (email) => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      console.log('[CandidatesPage] copied email', email);
      setToast('Email copied to clipboard');
    } catch (err) {
      console.error('[CandidatesPage] clipboard failed', err.message);
    }
  }, []);

  const handleNavigate = useCallback((to) => {
    console.log('[CandidatesPage] navigate ->', to);
    window.location.assign(to);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full space-y-6">
      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Directory</p>
          <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Candidates</h1>
          <p className="text-sm text-ink-faint mt-0.5">Browse interview history and risk per candidate.</p>
        </div>
        <span className="chip-teal font-mono self-start sm:self-auto">
          {visible.length} {visible.length === 1 ? 'candidate' : 'candidates'}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 sm:items-end max-w-3xl">
        <div className="flex-1 max-w-md">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="input"
          />
        </div>
        {role === 'SUPER_ADMIN' && (
          <div className="w-full max-w-xs">
            <label htmlFor="candidates-org-filter" className="block text-xs font-medium text-slate-600 mb-1">
              Organization
            </label>
            <select
              id="candidates-org-filter"
              className="input w-full"
              value={selectedOrgId}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedOrgId(v);
                const next = new URLSearchParams(searchParams);
                if (v) next.set('orgId', v);
                else next.delete('orgId');
                setSearchParams(next);
              }}
            >
              <option value="">All orgs</option>
              {orgList.map((o) => {
                const id = o.id ?? o.orgId;
                return (
                  <option key={id} value={id}>
                    {o.name || o.slug || id}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>

      {/* ── Table panel ──────────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16">
            <Spinner size="md" label="Loading candidates..." />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-red-600" role="alert">
            {error}
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            icon="👥"
            title={search ? 'No candidates match your search' : 'No candidates yet'}
            subtitle={
              search
                ? 'Try a different name or email.'
                : 'Start an interview session to see candidates here.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-10">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                  <SortHeader sortKey="totalSessions" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                    Sessions
                  </SortHeader>
                  <SortHeader sortKey="totalFindings" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                    Findings
                  </SortHeader>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Worst Risk</th>
                  <SortHeader sortKey="lastSeen" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                    Last Interview
                  </SortHeader>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((c, i) => {
                  const displayName = c.candidateName || nameFromEmail(c.candidateEmail);
                  return (
                    <tr
                      key={c.candidateEmail}
                      onClick={() => handleOpen(c)}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-xs text-slate-400">{i + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={displayName} email={c.candidateEmail} size="md" />
                          <span className="font-medium text-slate-900">{displayName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{c.candidateEmail}</td>
                      <td className="px-4 py-3 text-slate-700 font-medium">{c.totalSessions ?? 0}</td>
                      <td className="px-4 py-3 text-slate-700 font-medium">{c.totalFindings ?? 0}</td>
                      <td className="px-4 py-3">
                        {c.worstRisk
                          ? <Badge label={c.worstRisk} className={riskColor(c.worstRisk)} />
                          : <span className="text-slate-400 text-xs">-</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {relativeTime(c.lastSeen)}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => handleOpen(c)}
                          className="px-3 py-1.5 text-xs font-semibold text-teal-600
                                     hover:bg-teal-50 rounded transition-colors"
                        >
                          View Profile
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Profile panel ────────────────────────────────────────────── */}
      {selected && (
        <CandidateProfilePanel
          candidate={selected}
          allSessions={allSessions}
          allSchedules={allSchedules}
          loading={panelLoading}
          onClose={() => setSelected(null)}
          onCopy={handleCopyEmail}
          onNavigate={handleNavigate}
        />
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
